import { cn } from '@/lib/utils'

// ── Primitives ──────────────────────────────────────────────────────────────

/**
 * Base skeleton primitive. Renders an animated pulse placeholder.
 * Extend with className for dimensions, rounding, etc.
 *
 * @example
 *   <Skeleton className="h-4 w-48" />
 *   <Skeleton className="size-10 rounded-full" />
 */
export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'animate-pulse rounded-md bg-muted',
        className,
      )}
      {...props}
    />
  )
}

// ── Variants ────────────────────────────────────────────────────────────────

export interface SkeletonCardProps {
  /** Number of skeleton cards to render. Default 1. */
  count?: number
  className?: string
}

/**
 * Card-shaped skeleton — mimics a list item / dashboard card row.
 * Renders a rounded card with a leading icon placeholder, title line,
 * and two subtitle lines of varying width.
 */
export function SkeletonCard({ count = 1, className }: SkeletonCardProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex items-start gap-4 rounded-xl border border-border bg-card p-4"
        >
          {/* Leading icon */}
          <Skeleton className="size-10 shrink-0 rounded-lg" />
          {/* Text block */}
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          {/* Trailing chevron */}
          <Skeleton className="mt-1 size-4 shrink-0 rounded" />
        </div>
      ))}
    </div>
  )
}

export interface SkeletonTableRowProps {
  /** Number of columns per row. Default 5. */
  columns?: number
  /** Number of rows. Default 5. */
  count?: number
  className?: string
}

/**
 * Table-row skeletons — mimics a data table body.
 * Includes a header row and body rows, each with `columns` cells.
 */
export function SkeletonTableRow({
  columns = 5,
  count = 5,
  className,
}: SkeletonTableRowProps) {
  return (
    <div className={cn('rounded-xl border border-border bg-card overflow-hidden', className)}>
      {/* Header row */}
      <div className="flex items-center gap-4 border-b border-border bg-muted/50 px-4 py-3">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton
            key={`h-${i}`}
            className="h-3"
            style={{ width: `${[90, 60, 70, 55, 80, 65, 75, 50][i % 8]}px` }}
          />
        ))}
      </div>
      {/* Body rows */}
      {Array.from({ length: count }).map((_, rowIdx) => (
        <div
          key={rowIdx}
          className="flex items-center gap-4 border-b border-border/50 px-4 py-3 last:border-0"
        >
          {/* First cell: label + icon */}
          <div className="flex items-center gap-2">
            <Skeleton className="size-4 shrink-0 rounded" />
            <Skeleton className="h-4 w-28" />
          </div>
          {/* Remaining cells */}
          {Array.from({ length: columns - 1 }).map((_, cellIdx) => (
            <Skeleton
              key={`${rowIdx}-${cellIdx}`}
              className="h-4"
              style={{ width: `${[50, 65, 45, 75, 55, 60, 48][(rowIdx + cellIdx) % 7]}px` }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export interface SkeletonTextProps {
  /** Number of lines. Default 3. */
  lines?: number
  /** Width of the last line as a CSS value (e.g. "60%"). Default "75%". */
  lastLineWidth?: string
  className?: string
}

/**
 * Text-block skeleton — mimics a paragraph of text.
 * Each line is a full-width bar except the last, which is shorter.
 */
export function SkeletonText({
  lines = 3,
  lastLineWidth = '75%',
  className,
}: SkeletonTextProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-3.5"
          style={{
            width: i === lines - 1 ? lastLineWidth : `${[95, 88, 78, 92, 85][i % 5]}%`,
          }}
        />
      ))}
    </div>
  )
}

export interface SkeletonAvatarProps {
  /** Size variant. Default "md". */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
}

const avatarSizeMap: Record<NonNullable<SkeletonAvatarProps['size']>, string> = {
  sm: 'size-8',
  md: 'size-10',
  lg: 'size-14',
  xl: 'size-20',
}

/**
 * Avatar skeleton — a circular placeholder.
 * Use for user profiles, org logos, etc.
 */
export function SkeletonAvatar({ size = 'md', className }: SkeletonAvatarProps) {
  return (
    <Skeleton
      className={cn('shrink-0 rounded-full', avatarSizeMap[size], className)}
    />
  )
}
